import React from 'react';
import { useDispatch } from 'react-redux';
import Icon from '@cpg-its/ads.common.icon';
import Tooltip from '@material-ui/core/Tooltip';
import { yellow, darkBlue, blue } from '@cpg-its/ads.constants.colors';
import { Notification, NotificationPriority, NotificationStatus } from '../../pages/notification/notification-models';
import { dismissNotification } from '../../pages/notification/notification-helpers';
import { getFormattedDate } from '../../global/helpers/date-helpers';
import { MarkAsReadButton } from '../../global/components/mark-as-read-button';
import { WysiwygWithReactLink } from '../../global/components/wysiwyg-with-react-link';

interface Props {
	open: boolean;
	handleClose: () => void;
	notifications: Notification[];
	viewAllUrl: string;
	mandatoryTooltip?: string;
}

const REMOVE_ANIMATION_MS = 300;

const NotificationItem = ({ notification, onMarkRead, mandatoryTooltip }: { notification: Notification; onMarkRead: (n: Notification) => void; mandatoryTooltip?: string }) => {
	const [isRemoving, setIsRemoving] = React.useState(false);
	const isHighPriority = notification.priority === NotificationPriority.HIGH;
	const iconColor = isHighPriority ? yellow : darkBlue;
	const iconType = isHighPriority ? 'warning' : 'tooltip';

	const handleMarkRead = () => {
		if (isRemoving) return;
		setIsRemoving(true);
		window.setTimeout(() => onMarkRead(notification), REMOVE_ANIMATION_MS);
	};

	const itemClassName = `notifications-modal-item${notification.is_mandatory ? ' notifications-modal-item--mandatory' : ''}${isRemoving ? ' notifications-modal-item--removing' : ''}`;

	return (
		<div className={itemClassName}>
			<div className="notifications-modal-item-icon">
				<Icon color={iconColor} icon={iconType} size="large" />
			</div>
			<div className="notifications-modal-item-content">
				<h4 className="notifications-modal-item-subject">
					{notification.notification_subject}
				</h4>
				<div className="notifications-modal-item-body">
					<WysiwygWithReactLink content={notification.notification_body} />
				</div>
				<span className="notifications-modal-item-date">
					{getFormattedDate(notification.create_timestamp?.toString(), false, true)}
				</span>
			</div>
			{notification.is_mandatory ? (
				<span className="notification-item-mandatory-badge">
					<span className="notification-item-mandatory-label">Mandatory</span>
					<Tooltip title={mandatoryTooltip || 'This notification is mandatory and cannot be dismissed.'} arrow placement="top">
						<span className="notification-item-mandatory-info-icon">
							<Icon color={blue} icon="tooltip" size="small" />
						</span>
					</Tooltip>
				</span>
			) : (
				<MarkAsReadButton
					onClick={handleMarkRead}
					ariaLabel={`Mark as read: ${notification.notification_subject}`}
				/>
			)}
		</div>
	);
};

NotificationItem.defaultProps = {
	mandatoryTooltip: undefined,
};

export const NotificationsModal = (props: Props) => {
	const { open, handleClose, notifications, viewAllUrl, mandatoryTooltip } = props;
	const dispatch = useDispatch();

	if (!open) return null;

	const markAsRead = (notification: Notification) => {
		dismissNotification(dispatch, notification);
	};

	const visibleNotifications = (notifications || [])
		.filter((n) => n.notification_status === NotificationStatus.INITIAL)
		.sort(
			(a, b) =>
				new Date(b.create_timestamp || 0).getTime() -
				new Date(a.create_timestamp || 0).getTime(),
		)
		.slice(0, 5);

	return (
		<>
			<div
				className="notifications-modal-overlay"
				onClick={handleClose}
				aria-hidden="true"
			/>
			<div
				className="notifications-modal"
				role="dialog"
				aria-label="Notifications"
				aria-modal="true"
			>
				<div className="notifications-modal-header">
					<h2 className="notifications-modal-title">Notifications</h2>
				</div>

				<div className="notifications-modal-list">
					{visibleNotifications.length === 0 && (
						<div className="notifications-modal-empty">
							<p>You&apos;re all caught up.</p>
						</div>
					)}

					{visibleNotifications.map((notification) => (
						<NotificationItem
							key={notification.notification_id}
							notification={notification}
							onMarkRead={markAsRead}
							mandatoryTooltip={mandatoryTooltip}
						/>
					))}
				</div>

				<div className="notifications-modal-footer">
					<a href={viewAllUrl} className="notifications-modal-view-all">
						View All Notifications in MyCPG
					</a>
				</div>
			</div>
		</>
	);
};

NotificationsModal.defaultProps = {
	mandatoryTooltip: undefined,
};
